import { useEffect, useState, type ReactNode } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { SalesOrderDetailLink } from "@/components/hub/HubEntityDetailLink";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { useHubToast } from "@/components/ui/HubToast";
import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { formatRepAgencyRateDisplay } from "@/lib/repAgencyRates";
import type { InsideSalesSalesOrderRow } from "@/lib/insideSalesSalesOrders";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";
import { cn, formatTimestamp } from "@/lib/utils";
import type { RepAgencyPeekRow } from "@/components/inside-sales/RepAgencyDetailHoverHint";

export interface RepAgencyDetailRow extends RepAgencyPeekRow {
  commission_rate_tier_0_1000: string | null;
  commission_rate_tier_1001_5000: string | null;
  commission_rate_tier_5001_plus: string | null;
  mv_client_id: string | null;
  mv_client_name: string | null;
  mv_supplier_id: string | null;
  mv_supplier_type: string | null;
  sales_order_count: number;
  commission_statement_staff: Array<{ id: number; display_name: string; email: string }>;
}

const SALES_ORDERS_PAGE_SIZE = 25;
const EMPTY = "-";

function DetailField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-800">{value}</dd>
    </div>
  );
}

function integrationBadgeTone(ok: boolean): "success" | "warning" {
  return ok ? "success" : "warning";
}

function formatSalesOrderDate(submitted: string | null): string {
  if (submitted == null || submitted.trim() === "") {
    return EMPTY;
  }
  return formatTimestamp(submitted);
}

function freshsalesAccountUrl(accountId: string): string {
  return `https://smartgridsolutions.freshsales.io/accounts/${encodeURIComponent(accountId)}`;
}

function megaventorySupplierClientUrl(mvId: string, name: string): string {
  const label = encodeURIComponent(name.trim() || mvId);
  return `https://smartgridsolutions.megaventory.com/inventory/default.aspx?data=editSupplierClient&id=${encodeURIComponent(mvId)}&name=${label}`;
}

function ExternalEntityLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" className="text-sky-800 hover:underline">
      {children}
    </a>
  );
}

function displayOrEmpty(value: string | null | undefined): string {
  if (value == null || value.trim() === "") {
    return EMPTY;
  }
  return value;
}

interface RepAgencyStaffRow {
  id: number;
  display_name: string;
  email: string | null;
  work_phone: string | null;
  mobile_phone: string | null;
  on_distribution_list: boolean;
}

function statementKindLabel(kind: string): string {
  if (kind === "monthly_bonus") return "Monthly bonus";
  if (kind === "year_bonus") return "Year bonus";
  if (kind === "base") return "Base";
  return kind;
}

function statementMonthLabel(runMonth: string): string {
  const match = /^(\d{4})-(\d{2})/.exec(runMonth);
  if (match == null) return runMonth;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
}

function statementAmount(raw: string): string {
  const value = Number(raw);
  if (!Number.isFinite(value)) return raw;
  return value.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function RepAgencyStatementsTable({ agencyId }: { agencyId: number }) {
  const statementsQuery = useQuery<
    { rows: Array<{ id: number; run_month: string; kind: string; total_amount: string; status: string; sent_at: string | null }> },
    ApiError
  >({
    queryKey: ["inside-sales", "rep-agency", agencyId, "statements"],
    queryFn: () =>
      apiFetch(`/api/inside-sales/rep-agencies/${agencyId}/statements`),
    enabled: Number.isFinite(agencyId) && agencyId > 0,
  });
  const rows = statementsQuery.data?.rows ?? [];
  const pending = statementsQuery.isLoading && statementsQuery.data == null;

  return (
    <div className={cn("mt-6 border-t border-slate-100 pt-4", pending && "pointer-events-none opacity-40")}>
      {pending ? <p className="text-sm text-slate-600">Loading statements.</p> : null}
      {statementsQuery.isError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {statementsQuery.error.message}
        </p>
      ) : null}
      {!pending && !statementsQuery.isError && rows.length === 0 ? (
        <p className="text-sm text-slate-600">No commission statements yet.</p>
      ) : null}
      {rows.length > 0 ? (
        <div className="overflow-auto">
          <table className="w-full border-collapse text-left text-sm text-slate-800">
            <thead className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-1 py-2">Month</th>
                <th className="px-3 py-2">Kind</th>
                <th className="px-3 py-2">Amount</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Sent</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((statement) => (
                <tr key={statement.id} className="border-b border-slate-50 last:border-none">
                  <td className="px-1 py-2">{statementMonthLabel(statement.run_month)}</td>
                  <td className="px-3 py-2">{statementKindLabel(statement.kind)}</td>
                  <td className="px-3 py-2 tabular-nums">{statementAmount(statement.total_amount)}</td>
                  <td className="px-3 py-2">{statement.status}</td>
                  <td className="px-3 py-2">{statement.sent_at ? formatTimestamp(statement.sent_at) : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function staffPhone(workPhone: string | null, mobilePhone: string | null): string {
  const work = workPhone?.trim() ?? "";
  const mobile = mobilePhone?.trim() ?? "";
  if (work !== "" && mobile !== "" && work !== mobile) {
    return `${work}, ${mobile}`;
  }
  return work || mobile || "-";
}

interface StatementStaffChip {
  id: number;
  display_name: string;
  email: string;
}

function CommissionStatementListField({
  agencyId,
  staff,
  pending,
}: {
  agencyId: number;
  staff: StatementStaffChip[];
  pending: boolean;
}) {
  const canEdit = useHubModuleAdmin("inside_sales");
  const { showToast } = useHubToast();
  const queryClient = useQueryClient();
  const savedKey = staff.map((person) => person.id).join(",");
  const [chips, setChips] = useState(staff);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setChips(staff);
    setQuery("");
  }, [savedKey, staff]);
  const staffQuery = useQuery<{ rows: RepAgencyStaffRow[] }, ApiError>({
    queryKey: ["inside-sales", "rep-agency", agencyId, "staff"],
    queryFn: () => apiFetch<{ rows: RepAgencyStaffRow[] }>(`/api/inside-sales/rep-agencies/${agencyId}/staff`),
    enabled: Number.isFinite(agencyId) && agencyId > 0,
  });
  const selectedIds = new Set(chips.map((person) => person.id));
  const needle = query.trim().toLowerCase();
  const matches = (staffQuery.data?.rows ?? []).filter((person) => {
    const email = person.email?.trim() ?? "";
    if (email === "" || selectedIds.has(person.id)) {
      return false;
    }
    if (needle === "") {
      return true;
    }
    return `${person.display_name} ${email}`.toLowerCase().includes(needle);
  });
  const dirty = chips.map((person) => person.id).join(",") !== savedKey;
  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/api/inside-sales/rep-agencies/${agencyId}`, {
        method: "PATCH",
        body: JSON.stringify({ commissionStatementStaffIds: chips.map((person) => person.id) }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["inside-sales", "rep-agency", agencyId] });
      await queryClient.invalidateQueries({ queryKey: ["inside-sales", "rep-staff"] });
    },
    onError: (error) => {
      showToast({
        tone: "error",
        message: error instanceof Error ? error.message : "Could not save the distribution list.",
      });
    },
  });

  function addStaff(person: RepAgencyStaffRow): void {
    const email = person.email?.trim() ?? "";
    if (email === "") {
      return;
    }
    setChips((current) =>
      current.some((chip) => chip.id === person.id)
        ? current
        : [...current, { id: person.id, display_name: person.display_name, email }],
    );
    setQuery("");
    setOpen(false);
  }

  return (
    <div className="sm:col-span-2 lg:col-span-4">
      <div className="flex items-center gap-1">
        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500" htmlFor="commission-statement-staff">
          Commission statement distribution list
        </label>
        <HoverHintWithGlyph hoverHint="First person is the To line. Additional people are copied. Only staff on this agency who have an email can be added." />
      </div>
      <div className="relative mt-1">
        <div className="flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus-within:border-brand-500 focus-within:ring-1 focus-within:ring-brand-500">
          {chips.map((person) => (
            <span
              key={person.id}
              className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-800"
            >
              <span className="truncate" title={person.email}>
                {person.display_name}
              </span>
              {canEdit ? (
                <button
                  type="button"
                  className="rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
                  aria-label={`Remove ${person.display_name}`}
                  disabled={save.isPending}
                  onClick={() => setChips((current) => current.filter((chip) => chip.id !== person.id))}
                >
                  x
                </button>
              ) : null}
            </span>
          ))}
          {canEdit ? (
            <input
              id="commission-statement-staff"
              type="text"
              autoComplete="off"
              disabled={pending || save.isPending}
              value={query}
              placeholder={pending ? "Loading distribution list." : "Search agency staff"}
              className="min-w-[8rem] flex-1 border-0 bg-transparent px-1 py-1 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
              onChange={(event) => {
                setQuery(event.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
            />
          ) : null}
        </div>
        {open && canEdit ? (
          <div className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
            {matches.length === 0 ? (
              <p className="px-3 py-2 text-sm text-slate-600">No agency staff with an email match.</p>
            ) : (
              matches.slice(0, 12).map((person) => (
                <button
                  key={person.id}
                  type="button"
                  className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => addStaff(person)}
                >
                  <span className="font-medium text-slate-900">{person.display_name}</span>
                  <span className="truncate text-slate-500">{person.email}</span>
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>
      {save.isError ? (
        <p className="mt-2 text-sm text-rose-700">{save.error instanceof Error ? save.error.message : "Could not save."}</p>
      ) : null}
      {canEdit ? (
        <div className="mt-2">
          <HubSaveButton
            dirty={dirty && !pending}
            isLoading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save distribution list
          </HubSaveButton>
        </div>
      ) : null}
    </div>
  );
}

function RepAgencyStaffCard({
  agencyId,
  onOpenStaff,
}: {
  agencyId: number;
  onOpenStaff: (staffId: number) => void;
}) {
  const staffQuery = useQuery<{ rows: RepAgencyStaffRow[] }, ApiError>({
    queryKey: ["inside-sales", "rep-agency", agencyId, "staff"],
    queryFn: () => apiFetch<{ rows: RepAgencyStaffRow[] }>(`/api/inside-sales/rep-agencies/${agencyId}/staff`),
    enabled: Number.isFinite(agencyId) && agencyId > 0,
  });
  const staff = staffQuery.data?.rows ?? [];
  const pending = staffQuery.isLoading;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Staff</CardTitle>
      </CardHeader>
      <CardBody className={cn("p-0", pending && "pointer-events-none opacity-40")}>
        {pending ? <p className="px-5 py-4 text-sm text-slate-600">Loading staff.</p> : null}
        {staffQuery.isError ? (
          <p className="mx-5 my-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {staffQuery.error.message}
          </p>
        ) : null}
        {!pending && !staffQuery.isError && staff.length === 0 ? (
          <p className="px-5 py-4 text-sm text-slate-600">No staff found for this rep agency.</p>
        ) : null}
        {staff.length > 0 ? (
          <div className="overflow-auto">
            <table className="w-full border-collapse text-left text-sm text-slate-800">
              <thead className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-5 py-2">Name</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Phone</th>
                </tr>
              </thead>
              <tbody>
                {staff.map((person) => (
                  <tr key={person.id} className="border-b border-slate-50 last:border-none">
                    <td className="px-5 py-2 align-top">
                      <button
                        type="button"
                        className="font-medium text-sky-800 hover:underline"
                        onClick={() => onOpenStaff(person.id)}
                      >
                        {person.display_name}
                      </button>
                    </td>
                    <td className="px-3 py-2 align-top">{person.email?.trim() || "-"}</td>
                    <td className="px-3 py-2 align-top">{staffPhone(person.work_phone, person.mobile_phone)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function RepAgencySalesOrdersCard({ agencyId, totalCount }: { agencyId: number; totalCount: number | null }) {
  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
  }, [agencyId]);

  const salesOrdersQuery = useQuery<{ rows: InsideSalesSalesOrderRow[]; total_count: number }, ApiError>({
    queryKey: ["inside-sales", "rep-agency", agencyId, "sales-orders", page],
    queryFn: () =>
      apiFetch<{ rows: InsideSalesSalesOrderRow[]; total_count: number }>(
        `/api/inside-sales/rep-agencies/${agencyId}/sales-orders`,
        { query: { limit: String(SALES_ORDERS_PAGE_SIZE), offset: String(page * SALES_ORDERS_PAGE_SIZE) } },
      ),
    enabled: Number.isFinite(agencyId) && agencyId > 0,
    placeholderData: keepPreviousData,
  });

  const rows = salesOrdersQuery.data?.rows ?? [];
  const loadedTotal = salesOrdersQuery.data?.total_count ?? totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(loadedTotal / SALES_ORDERS_PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pending = salesOrdersQuery.isLoading && salesOrdersQuery.data == null;

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>Sales orders</CardTitle>
        {loadedTotal > 0 && !pending ? (
          <span className="text-xs font-normal text-slate-500">
            {`${loadedTotal.toLocaleString("en-US")} active`}
          </span>
        ) : null}
      </CardHeader>
      <CardBody className={cn("space-y-3 p-0", pending && "pointer-events-none opacity-40")}>
        {pending ? <p className="px-5 py-4 text-sm text-slate-600">Loading sales orders.</p> : null}
        {salesOrdersQuery.isError ? (
          <p className="mx-5 my-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {salesOrdersQuery.error.message}
          </p>
        ) : null}
        {!pending && !salesOrdersQuery.isError && rows.length === 0 ? (
          <p className="px-5 py-4 text-sm text-slate-600">No active sales orders for this rep agency.</p>
        ) : null}
        {rows.length > 0 ? (
          <div className="max-h-[28rem] overflow-auto">
            <table className="w-full border-collapse text-left text-sm text-slate-800">
              <thead className="sticky top-0 z-10 border-b border-slate-200 bg-white text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-5 py-2">Sales order</th>
                  <th className="px-3 py-2">Submitted</th>
                  <th className="px-3 py-2">Customer</th>
                  <th className="px-3 py-2">End user</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-b border-slate-50 last:border-none hover:bg-slate-50/80">
                    <td className="px-5 py-2 align-top">
                      <SalesOrderDetailLink
                        salesOrderId={row.id}
                        salesOrderNumber={row.number}
                        peek={{
                          sales_order_number: row.number,
                          client_name: row.client_fs_account_name,
                          end_user_name: row.end_user_fs_account_name,
                        }}
                      />
                    </td>
                    <td className="px-3 py-2 align-top text-slate-700">{formatSalesOrderDate(row.submitted)}</td>
                    <td className="px-3 py-2 align-top text-slate-700">{row.client_fs_account_name ?? EMPTY}</td>
                    <td className="px-3 py-2 align-top text-slate-700">{row.end_user_fs_account_name ?? EMPTY}</td>
                    <td className="px-3 py-2 align-top">
                      {row.status ? <Badge tone="neutral">{row.status}</Badge> : EMPTY}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {totalPages > 1 ? (
          <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-5 py-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={safePage <= 0 || salesOrdersQuery.isFetching}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              Previous
            </Button>
            <span className="text-sm text-slate-600">
              {`Page ${(safePage + 1).toLocaleString("en-US")} of ${totalPages.toLocaleString("en-US")}`}
            </span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={safePage >= totalPages - 1 || salesOrdersQuery.isFetching}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </Button>
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

interface RepStaffProfile {
  id: number;
  display_name: string;
  email: string | null;
  work_phone: string | null;
  mobile_phone: string | null;
  tags: string[];
  freshsales_fields: Array<{ label: string; value: string }>;
  on_distribution_list: boolean;
  agency_name: string | null;
}

function RepStaffProfilePanel({ agencyId, staffId }: { agencyId: number; staffId: number }) {
  const profileQuery = useQuery<{ row: RepStaffProfile }, ApiError>({
    queryKey: ["inside-sales", "rep-staff", staffId, agencyId],
    queryFn: () =>
      apiFetch<{ row: RepStaffProfile }>(`/api/inside-sales/rep-staff/${staffId}`, {
        query: { agencyId: String(agencyId) },
      }),
    enabled: staffId > 0,
  });
  const profile = profileQuery.data?.row;
  const pending = profileQuery.isLoading && profile == null;

  return (
    <div className={cn("space-y-4", pending && "pointer-events-none opacity-40")}>
      {pending ? <p className="text-sm text-slate-600">Loading profile.</p> : null}
      {profileQuery.isError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {profileQuery.error.message}
        </p>
      ) : null}
      {profile != null ? (
        <Card>
          {profile.on_distribution_list ? (
            <CardHeader className="flex flex-row flex-wrap items-center gap-2">
              <Badge tone="info">Distribution list</Badge>
            </CardHeader>
          ) : null}
          <CardBody>
            <dl className="grid gap-4 sm:grid-cols-2">
              <DetailField label="Email" value={displayOrEmpty(profile.email)} />
              <DetailField label="Phone" value={staffPhone(profile.work_phone, profile.mobile_phone)} />
              <DetailField label="Rep agency" value={displayOrEmpty(profile.agency_name)} />
              <DetailField
                label="Tags"
                value={profile.tags.length > 0 ? profile.tags.join(", ") : "-"}
              />
              {profile.freshsales_fields.map((field) => (
                <DetailField key={field.label} label={field.label} value={field.value} />
              ))}
            </dl>
          </CardBody>
        </Card>
      ) : null}
    </div>
  );
}

export type AgencyDetailTab = "information" | "staff" | "sales-orders";

export function RepAgencyDetailPanel({
  agencyId,
  staffId,
  tab,
  onOpenStaff,
}: {
  agencyId: number;
  staffId: number | null;
  tab: AgencyDetailTab;
  onOpenStaff: (staffId: number) => void;
}) {
  const detailQuery = useQuery<{ row: RepAgencyDetailRow }, ApiError>({
    queryKey: ["inside-sales", "rep-agency", agencyId],
    queryFn: () => apiFetch<{ row: RepAgencyDetailRow }>(`/api/inside-sales/rep-agencies/${agencyId}`),
    enabled: Number.isFinite(agencyId) && agencyId > 0,
  });

  const row = detailQuery.data?.row;
  const detailPending = detailQuery.isLoading && row == null;

  if (staffId != null) {
    return <RepStaffProfilePanel agencyId={agencyId} staffId={staffId} />;
  }

  return (
    <div className="space-y-4">
      {detailQuery.isError ? (
        <p className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {detailQuery.error.message}
        </p>
      ) : null}
      {tab === "staff" ? <RepAgencyStaffCard agencyId={agencyId} onOpenStaff={onOpenStaff} /> : null}
      {tab === "sales-orders" ? (
        <RepAgencySalesOrdersCard agencyId={agencyId} totalCount={row?.sales_order_count ?? null} />
      ) : null}
      {tab === "information" ? (
        <div className="space-y-6">
          <section className={cn("space-y-3", detailPending && "pointer-events-none opacity-40")}>
            <div className="flex flex-wrap items-center gap-2">
              {detailPending ? <Badge tone="muted">Loading status.</Badge> : null}
              {row != null ? (
                <>
                  <Badge tone={integrationBadgeTone(row.integration.has_freshsales_id)}>
                    {row.integration.has_freshsales_id ? "Freshsales linked" : "Freshsales missing"}
                  </Badge>
                  <Badge tone={integrationBadgeTone(row.integration.has_megaventory_supplier)}>
                    {row.integration.has_megaventory_supplier ? "MV supplier linked" : "MV supplier missing"}
                  </Badge>
                  {row.integration.has_megaventory_customer ? <Badge tone="info">MV customer linked</Badge> : null}
                  {row.commission_terminated_at ? (
                    <Badge tone="danger">Terminated {row.commission_terminated_at}</Badge>
                  ) : null}
                </>
              ) : null}
            </div>
          </section>
          <Card>
            <CardHeader>
              <CardTitle>Integration links</CardTitle>
            </CardHeader>
            <CardBody className={cn(detailPending && "pointer-events-none opacity-40")}>
              <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <DetailField
                  label="Freshsales account"
                  value={
                    detailPending
                      ? EMPTY
                      : row?.fs_account_id != null && row.fs_account_id.trim() !== ""
                        ? (
                            <ExternalEntityLink href={freshsalesAccountUrl(row.fs_account_id)}>
                              {row.fs_account_name?.trim() || row.fs_account_id} ({row.fs_account_id})
                            </ExternalEntityLink>
                          )
                        : displayOrEmpty(row?.fs_account_name)
                  }
                />
                <DetailField
                  label="Megaventory supplier"
                  value={
                    detailPending
                      ? EMPTY
                      : row?.mv_supplier_id != null && row.mv_supplier_id.trim() !== ""
                        ? (
                            <ExternalEntityLink
                              href={megaventorySupplierClientUrl(row.mv_supplier_id, row.mv_supplier_name ?? "")}
                            >
                              {row.mv_supplier_name?.trim() || row.mv_supplier_id}
                              {row.mv_supplier_type ? ` - ${row.mv_supplier_type}` : ""}
                            </ExternalEntityLink>
                          )
                        : displayOrEmpty(row?.mv_supplier_name)
                  }
                />
                <DetailField
                  label="Megaventory customer"
                  value={
                    detailPending
                      ? EMPTY
                      : row?.mv_client_id != null && row.mv_client_id.trim() !== ""
                        ? (
                            <ExternalEntityLink
                              href={megaventorySupplierClientUrl(row.mv_client_id, row.mv_client_name ?? "")}
                            >
                              {row.mv_client_name?.trim() || row.mv_client_id}
                            </ExternalEntityLink>
                          )
                        : displayOrEmpty(row?.mv_client_name)
                  }
                />
                <DetailField label="QBO vendor id" value={detailPending ? EMPTY : displayOrEmpty(row?.qbo_vendor_id)} />
              </dl>
            </CardBody>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Commissions</CardTitle>
            </CardHeader>
            <CardBody className={cn(detailPending && "pointer-events-none opacity-40")}>
              <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <DetailField
                  label="Base rate"
                  value={detailPending ? EMPTY : formatRepAgencyRateDisplay(row?.commission_rate ?? null)}
                />
                <DetailField
                  label="0-1000 tier"
                  value={detailPending ? EMPTY : formatRepAgencyRateDisplay(row?.commission_rate_tier_0_1000 ?? null)}
                />
                <DetailField
                  label="1001-5000 tier"
                  value={detailPending ? EMPTY : formatRepAgencyRateDisplay(row?.commission_rate_tier_1001_5000 ?? null)}
                />
                <DetailField
                  label="5001+ tier"
                  value={detailPending ? EMPTY : formatRepAgencyRateDisplay(row?.commission_rate_tier_5001_plus ?? null)}
                />
                <DetailField
                  label="Terminated on"
                  value={detailPending ? EMPTY : displayOrEmpty(row?.commission_terminated_at)}
                />
                <CommissionStatementListField
                  agencyId={agencyId}
                  staff={row?.commission_statement_staff ?? []}
                  pending={detailPending}
                />
              </dl>
              <RepAgencyStatementsTable agencyId={agencyId} />
            </CardBody>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
